import React from "@platform/react";
import { isRecord } from "@platform/types/record";

const REPL = "%" as const;

export const NAME_RESIZEHANDLER_HOOK = "useResizeWindow" as const;

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const THROTTLE_RESIZEHANDLER_HOOK = 500 as const;

export const ERROR_RESIZEHANDLER_HOOK = {
    NO_HANDLER: `${NAME_RESIZEHANDLER_HOOK}: A handler function is expected, got: ${REPL}`,
    INVALID_TARGET: `${NAME_RESIZEHANDLER_HOOK}: Expecting Window or HTMLElement as target, got: ${REPL} `,
} as const;

export type SizeResizeHandlerHook = {
    width: number;
    height: number;
};

export type ArgResizeHandler = {
    handler: (size: SizeResizeHandlerHook) => void;
    target?: Window | HTMLElement | null;
    throttle?: number;
};

export function useResizeHandler(arg: ArgResizeHandler): void {
    const { handler, target, throttle = THROTTLE_RESIZEHANDLER_HOOK } = arg;
    React.useEffect(() => {
        if (!target) return undefined;

        if (isWindow(target)) {
            let timeout: NodeJS.Timeout | null = null;
            const rootHandler = () => {
                handleValidations();
                if (timeout) clearTimeout(timeout);
                timeout = setTimeout(
                    () => handler({ height: window.innerHeight, width: window.innerWidth }),
                    throttle,
                );
            };
            target.addEventListener("resize", rootHandler);
            rootHandler();
            return () => target.removeEventListener("resize", rootHandler);
        }

        if (isHTMLElement(target)) {
            let timeout: NodeJS.Timeout | null = null;
            const rootHandler = (entries: ResizeObserverEntry[]) => {
                handleValidations();
                if (timeout) clearTimeout(timeout);
                timeout = setTimeout(() => {
                    for (const entry of entries) {
                        const { width, height } = entry.contentRect;
                        handler({ width, height });
                    }
                }, throttle);
            };
            const resizer = new ResizeObserver(rootHandler);
            resizer.observe(target);
            return () => {
                resizer.unobserve(target);
                resizer.disconnect();
            };
        }

        throw new Error(ERROR_RESIZEHANDLER_HOOK.INVALID_TARGET.replace(REPL, typeof target));

        function handleValidations() {
            if (typeof handler !== "function") {
                throw new Error(ERROR_RESIZEHANDLER_HOOK.NO_HANDLER.replace(REPL, typeof handler));
            }
        }
    }, [handler, target, throttle]);
}

export function isWindow(entry: unknown): entry is Window {
    if (!isRecord(entry)) return false;
    if (!(entry instanceof Window)) return false;
    return true;
}

export function isHTMLElement(entry: unknown): entry is HTMLElement {
    if (!isRecord(entry)) return false;
    if (!(entry instanceof HTMLElement)) return false;
    return true;
}
